// Tipos de estado de pedido
export type OrderStatus =
  | 'por_confirmar'
  | 'por_preparar'
  | 'en_preparacion'
  | 'preparado'
  | 'enviado'
  | 'devuelto'
  | 'cancelado';

// Configuración de botones del toolbar de estados
export interface ToolbarButton {
  key: string;
  link: string;
  status_id?: number;
}

// Configuración de acciones disponibles por estado
export interface StatusActions {
  cancel: boolean;
  prepare: boolean;
  markForSend: boolean;
  uncancel: boolean;
  view: boolean;
  updateFromStore: boolean;
  duplicate: boolean;
}

// Configuración de acciones del dropdown
export interface DropdownAction {
  id: string;
  icon: any;
  label: string;
  action: string;
  requiresConfirmation: boolean;
  confirmationKey?: string;
}

// Mapeo de códigos de estado a estados del dropdown
export const getStatusForDropdown = (statusCode: number): OrderStatus => {
  switch (statusCode) {
    case 1:
      return 'por_confirmar';
    case 2:
    case 3:
      return 'por_preparar';
    case 4:
    case 5:
      return 'en_preparacion';
    case 6:
      return 'preparado';
    case 7:
      return 'enviado';
    case 8:
      return 'devuelto';
    case 9:
      return 'cancelado';
    default:
      return 'por_confirmar';
  }
};

// Configuración de acciones disponibles por estado
export const getAvailableActionsByStatus = (
  status: OrderStatus
): StatusActions => {
  switch (status) {
    case 'por_confirmar':
      return {
        cancel: true,
        prepare: false,
        markForSend: true,
        uncancel: false,
        view: true,
        updateFromStore: false,
        duplicate: false,
      };
    case 'por_preparar':
      return {
        cancel: true,
        prepare: false,
        markForSend: false,
        uncancel: false,
        view: true,
        updateFromStore: false,
        duplicate: false,
      };
    case 'en_preparacion':
      return {
        cancel: false,
        prepare: false,
        markForSend: false,
        uncancel: false,
        view: true,
        updateFromStore: false,
        duplicate: false,
      };
    case 'preparado':
      return {
        cancel: false,
        prepare: false,
        markForSend: false,
        uncancel: false,
        view: true,
        updateFromStore: false,
        duplicate: false,
      };
    case 'enviado':
      return {
        cancel: false,
        prepare: false,
        markForSend: false,
        uncancel: false,
        view: true,
        updateFromStore: false,
        duplicate: false,
      };
    case 'devuelto':
      return {
        cancel: false,
        prepare: false,
        markForSend: false,
        uncancel: false,
        view: true,
        updateFromStore: false,
        duplicate: false,
      };
    case 'cancelado':
      return {
        cancel: false,
        prepare: false,
        markForSend: false,
        uncancel: true,
        view: true,
        updateFromStore: false,
        duplicate: false,
      };
    default:
      return {
        cancel: false,
        prepare: false,
        markForSend: false,
        uncancel: false,
        view: true,
        updateFromStore: false,
        duplicate: false,
      };
  }
};

// Configuración de botones del toolbar
export const toolbarButtons: ToolbarButton[] = [
  { key: 'all', link: '#' },
  { key: 'to_confirm', link: '#', status_id: 1 },
  { key: 'pending', link: '#', status_id: 2 },
  { key: 'to_prepare', link: '#', status_id: 3 },
  { key: 'in_preparation', link: '#', status_id: 4 }, // Picking
  { key: 'prepared', link: '#', status_id: 6 },
  { key: 'sent', link: '#', status_id: 7 },
  { key: 'returned', link: '#', status_id: 8 },
  { key: 'cancelled', link: '#', status_id: 9 },
];

/** Opciones de estado del pedido para filtros (sin API, mismos que Orders) */
export const orderStatusFilterOptions: { status_id: number; labelKey: string }[] = [
  { status_id: 1, labelKey: 'toolbar.to_confirm' },
  { status_id: 2, labelKey: 'toolbar.pending' },
  { status_id: 3, labelKey: 'toolbar.to_prepare' },
  { status_id: 4, labelKey: 'toolbar.in_preparation' },
  { status_id: 5, labelKey: 'toolbar.in_preparation_packing' },
  { status_id: 6, labelKey: 'toolbar.prepared' },
  { status_id: 7, labelKey: 'toolbar.sent' },
  { status_id: 8, labelKey: 'toolbar.returned' },
  { status_id: 9, labelKey: 'toolbar.cancelled' },
];

// Configuración de acciones del dropdown por estado
export const getDropdownActionsByStatus = (
  status: OrderStatus,
  t: (key: string) => string,
  icons: {
    faCircleXmark: any;
    faArrowsRotate: any;
    faCircleCheck: any;
    faCopy: any;
    faSquareMinus: any;
    faEye: any;
    faTag: any;
    faPrint: any;
    faXmark: any;
  },
  hasUnassignStockPermission: boolean = true
): DropdownAction[] => {
  switch (status) {
    case 'por_confirmar':
      return [
        {
          id: 'view_order',
          icon: icons.faEye,
          label: t('actions.view_order'),
          action: 'view_order',
          requiresConfirmation: true,
        },
        {
          id: 'cancel',
          icon: icons.faCircleXmark,
          label: t('actions.cancel'),
          action: 'cancel',
          requiresConfirmation: true,
          confirmationKey: 'cancel',
        },
        {
          id: 'mark_send',
          icon: icons.faCircleCheck,
          label: t('actions.mark_for_send'),
          action: 'mark_send',
          requiresConfirmation: true,
          confirmationKey: 'markForSend',
        },
        {
          id: 'duplicate',
          icon: icons.faCopy,
          label: t('actions.duplicate'),
          action: 'duplicate',
          requiresConfirmation: true,
        },
      ];

    case 'por_preparar':
      const porPrepararActions = [
        {
          id: 'view_order',
          icon: icons.faEye,
          label: t('actions.view_order'),
          action: 'view_order',
          requiresConfirmation: true,
        },
        {
          id: 'cancel',
          icon: icons.faCircleXmark,
          label: t('actions.cancel'),
          action: 'cancel',
          requiresConfirmation: true,
          confirmationKey: 'cancel',
        },
      ];

      // Solo agregar la acción de desasignar stock si el usuario tiene permisos
      if (hasUnassignStockPermission) {
        porPrepararActions.push({
          id: 'unassign_stock',
          icon: icons.faSquareMinus,
          label: t('actions.unassign_stock'),
          action: 'unassign_stock',
          requiresConfirmation: true,
          confirmationKey: 'unassignStock',
        });
      }

      porPrepararActions.push(
        {
          id: 'labels',
          icon: icons.faTag,
          label: t('actions.labels'),
          action: 'labels',
          requiresConfirmation: true,
        },
        {
          id: 'duplicate',
          icon: icons.faCopy,
          label: t('actions.duplicate'),
          action: 'duplicate',
          requiresConfirmation: true,
        }
      );

      return porPrepararActions;

    case 'en_preparacion':
      return [
        {
          id: 'view_order',
          icon: icons.faEye,
          label: t('actions.view_order'),
          action: 'view_order',
          requiresConfirmation: true,
        },
        {
          id: 'labels',
          icon: icons.faTag,
          label: t('actions.labels'),
          action: 'labels',
          requiresConfirmation: true,
        },
        {
          id: 'duplicate',
          icon: icons.faCopy,
          label: t('actions.duplicate'),
          action: 'duplicate',
          requiresConfirmation: true,
        },
      ];

    case 'preparado':
      return [
        {
          id: 'view_order',
          icon: icons.faEye,
          label: t('actions.view_order'),
          action: 'view_order',
          requiresConfirmation: true,
        },
        {
          id: 'labels',
          icon: icons.faTag,
          label: t('actions.labels'),
          action: 'labels',
          requiresConfirmation: true,
        },
        {
          id: 'duplicate',
          icon: icons.faCopy,
          label: t('actions.duplicate'),
          action: 'duplicate',
          requiresConfirmation: true,
        },
      ];

    case 'enviado':
      return [
        {
          id: 'view_order',
          icon: icons.faEye,
          label: t('actions.view_order'),
          action: 'view_order',
          requiresConfirmation: true,
        },
        {
          id: 'labels',
          icon: icons.faTag,
          label: t('actions.labels'),
          action: 'labels',
          requiresConfirmation: true,
        },
        {
          id: 'duplicate',
          icon: icons.faCopy,
          label: t('actions.duplicate'),
          action: 'duplicate',
          requiresConfirmation: true,
        },
      ];

    case 'devuelto':
      return [
        {
          id: 'view_order',
          icon: icons.faEye,
          label: t('actions.view_order'),
          action: 'view_order',
          requiresConfirmation: true,
        },
        {
          id: 'labels',
          icon: icons.faTag,
          label: t('actions.labels'),
          action: 'labels',
          requiresConfirmation: true,
        },
        {
          id: 'duplicate',
          icon: icons.faCopy,
          label: t('actions.duplicate'),
          action: 'duplicate',
          requiresConfirmation: true,
        },
      ];

    case 'cancelado':
      return [
        {
          id: 'view_order',
          icon: icons.faEye,
          label: t('actions.view_order'),
          action: 'view_order',
          requiresConfirmation: true,
        },
        {
          id: 'uncancel',
          icon: icons.faXmark,
          label: t('actions.uncancel'),
          action: 'uncancel',
          requiresConfirmation: true,
          confirmationKey: 'uncancel',
        },
        {
          id: 'duplicate',
          icon: icons.faCopy,
          label: t('actions.duplicate'),
          action: 'duplicate',
          requiresConfirmation: true,
        },
      ];

    default:
      return [
        {
          id: 'view_order',
          icon: icons.faEye,
          label: t('actions.view_order'),
          action: 'view_order',
          requiresConfirmation: true,
        },
      ];
  }
};

// Configuración de colores de estado
export const statusColorMap: Record<number, { bg: string; text: string }> = {
  1: {
    // Por confirmar
    bg: 'bg-card-border light:bg-badge-bg-status-2',
    text: 'text-badge-status-2',
  },
  2: {
    // Pendiente
    bg: 'bg-card-border light:bg-badge-bg-status-1',
    text: 'text-badge-status-1',
  },
  3: {
    // Por preparar
    bg: 'bg-card-border light:bg-badge-bg-status-3',
    text: 'text-badge-status-3',
  },
  4: {
    // En preparación (picking)
    bg: 'bg-card-border light:bg-bg-warning',
    text: 'text-warning',
  },
  5: {
    // En preparación (packing)
    bg: 'bg-bg-info',
    text: 'text-info',
  },
  6: {
    // Preparado
    bg: 'bg-card-border light:bg-badge-status-4',
    text: 'text-badge-status-4 light:text-badge-bg-status-4',
  },
  7: {
    // Enviado
    bg: 'bg-bg-success',
    text: 'text-success',
  },
  8: {
    // Devuelto
    bg: 'bg-card-border',
    text: '',
  },
  9: {
    // Cancelado
    bg: 'bg-bg-error',
    text: 'text-error',
  },
};

// Configuración de colores para estados logísticos (envío)
export const logisticStatusColorMap: Record<number, { bg: string; text: string }> = {
  1: {
    // Sin estado logístico
    bg: 'bg-card-border',
    text: 'text-text',
  },
  2: {
    // En tránsito
    bg: 'bg-card-border light:bg-bg-warning',
    text: 'text-warning',
  },
  3: {
    // En reparto
    bg: 'bg-card-border light:bg-bg-info',
    text: 'text-info',
  },
  4: {
    // Punto de recogida
    bg: 'bg-card-border light:bg-badge-status-4',
    text: 'text-badge-status-4',
  },
  5: {
    // Entregado
    bg: 'bg-bg-success',
    text: 'text-success',
  },
  6: {
    // Devuelto al remitente
    bg: 'bg-card-border light:bg-bg-error',
    text: 'text-error',
  },
};

// Configuración de iconos para las acciones de selección
export const getSelectionActionIcons = (icons: {
  faXmarkCircle: any;
  faBoxOpen: any;
  faCircleCheck: any;
  faXmark: any;
}) => ({
  cancel: icons.faXmarkCircle,
  prepare: icons.faBoxOpen,
  markForSend: icons.faCircleCheck,
  uncancel: icons.faXmark,
});
